import React, { useEffect, useRef, useState } from "react";
import {
	Header,
	HeaderContainer,
	MobileNavLinksContainer,
	DesktopNavLinks,
	MobileNavLinks,
	MobileMenusWrapper,
	NavToggle,
	LogoLink,
} from "./styles";
import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
import { useTranslation } from "react-i18next"; // Import useTranslation hook
import useAnimatedNavToggler from "../../helpers/useAnimatedNavToggler";
import LanguageSwitcher from "./LanguageSwitcher";
import DefaultNavLinks from "./NavLinks";

import logo from "../../images/mylogo.png";

const HeaderComponent = ({
	roundedHeaderButton = false,
	logoLink,
	links,
	className,
}) => {
	const { showNavLinks, animation, toggleNavbar } = useAnimatedNavToggler();
	const menuRef = useRef(null);
	const navToggleRef = useRef(null); // New ref for NavToggle
	const [activeSection, setActiveSection] = useState("");
	const activeSectionRef = useRef(activeSection); // Ref to hold activeSection value

	// i18n Translation
	const { t } = useTranslation(); // Use the translation hook

	// Update the ref whenever activeSection changes
	useEffect(() => {
		activeSectionRef.current = activeSection;
	}, [activeSection]);

	// Handle body scroll when mobile menu is open
	useEffect(() => {
		document.body.style.overflow = showNavLinks ? "hidden" : "auto";
	}, [showNavLinks]);

	// Scroll position tracking
	useEffect(() => {
		const handleScroll = () => {
			const sections = [
				"home",
				"services",
				"practice",
				"about",
				"expertise",
				"contact",
			];
			let currentSection = "";
			const scrollPosition = window.scrollY + window.innerHeight / 3;
			const pageHeight = document.documentElement.scrollHeight;
			const bottomOffset = 100; // Adjust this value based on how sensitive you want the detection to be

			// Detect when the user is near the bottom of the page
			const isBottom =
				window.innerHeight + window.scrollY >= pageHeight - bottomOffset;

			// If user is at the bottom, mark 'contact' as the active section
			if (isBottom) {
				currentSection = "contact";
			} else {
				// Regular section detection
				for (let i = 0; i < sections.length; i++) {
					const section = document.getElementById(sections[i]);
					if (section) {
						const rect = section.getBoundingClientRect();
						const sectionTop = rect.top + window.scrollY;
						const sectionHeight = section.offsetHeight;

						if (
							scrollPosition >= sectionTop &&
							scrollPosition < sectionTop + sectionHeight
						) {
							currentSection = sections[i];
							break;
						}
					}
				}
			}

			if (currentSection !== activeSectionRef.current) {
				setActiveSection(currentSection);
			}
		};

		window.addEventListener("scroll", handleScroll);
		handleScroll(); // Call initially to set the active section on page load

		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, []); // Empty dependency array ensures this runs once

	// Close mobile menu when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				menuRef.current &&
				!menuRef.current.contains(event.target) &&
				navToggleRef.current &&
				!navToggleRef.current.contains(event.target) &&
				showNavLinks
			) {
				toggleNavbar();
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		document.addEventListener("touchstart", handleClickOutside);

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			document.removeEventListener("touchstart", handleClickOutside);
		};
	}, [showNavLinks, toggleNavbar]);

	// Default logo link with i18n translation for "Treact"
	const defaultLogoLink = (
		<LogoLink href="/">
			<img src={logo} alt="logo" />
			{t("header.logoName")} {/* i18n translation for logo text */}
		</LogoLink>
	);

	logoLink = logoLink || defaultLogoLink;
	links = links || (
		<DefaultNavLinks
			activeSection={activeSection}
			closeMobileMenu={() => {
				if (showNavLinks) toggleNavbar();
			}}
		/>
	);

	return (
		<Header className={className || "header-light"}>
			<HeaderContainer>
				<DesktopNavLinks>
					{logoLink}
					{links}
					<LanguageSwitcher />
				</DesktopNavLinks>

				<MobileNavLinksContainer>
					{logoLink}
					<MobileNavLinks
						ref={menuRef}
						initial={{ x: "150%", display: "none" }}
						animate={animation}
					>
						{links}
					</MobileNavLinks>
					<MobileMenusWrapper isMenuOpen={showNavLinks}>
						<LanguageSwitcher />
						<NavToggle
							ref={navToggleRef}
							onClick={(event) => {
								event.stopPropagation(); // Prevent click event from bubbling up
								toggleNavbar();
							}}
							className={showNavLinks ? "open" : "closed"}
						>
							{showNavLinks ? (
								<CloseIcon tw="w-6 h-6" />
							) : (
								<MenuIcon tw="w-6 h-6" />
							)}
						</NavToggle>
					</MobileMenusWrapper>
				</MobileNavLinksContainer>
			</HeaderContainer>
		</Header>
	);
};

export default HeaderComponent;
